import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { setClassNames } from '@ddc/classnames-js';
import { useLabels } from 'ws-scripts/modules/common';
import { useCurrentPath } from '../context/CurrentPathContext';
import {
	buildNavUrl,
	isNavItemActive,
	stripHtmlToText
} from '../utils/navItemUtils';
import useTrackEvent from '../hooks/useTrackEvent';
import {
	DRAWER_EVENT,
	NAV_DEPTH,
	TRACKING_CONFIG,
	WIDGET_STATE
} from '../utils/trackEvent';
import NavItemLabel from './NavItemLabel';

/**
 * NavLink — renders a simple anchor link for a navigation item.
 *
 * When rendered via NavItem (top-level), isActive/isLeafActive are passed
 * from the orchestrator. When rendered directly by DrilldownLeafLink or
 * DrilldownGroup, NavLink computes them internally as a fallback.
 *
 * @param {Object} props
 * @param {NavigationItem} props.item - Navigation item data
 * @param {() => void} props.onLinkClick - Called when the link is clicked
 * @param {boolean} [props.isActive] - Whether this item or an ancestor is active (from orchestrator)
 * @param {boolean} [props.isLeafActive] - Whether this specific item is the current page (from orchestrator)
 * @param {string} [props.labelClassName] - Additional class name applied to the label span
 * @param {number} [props.depth] - Navigation depth reported to analytics; one of
 *   NAV_DEPTH. Defaults to the top level, which is where a NavLink rendered
 *   without an explicit depth (via NavItem) always sits.
 */
const NavLink = ({
	item,
	onLinkClick,
	isActive,
	isLeafActive,
	labelClassName = '',
	depth = NAV_DEPTH.TOP_LEVEL
}) => {
	const labels = useLabels();
	const track = useTrackEvent();
	const opensInNewTabLabel = labels.get('OPENS_IN_NEW_TAB');
	const currentPath = useCurrentPath();

	// Fall back to self-computed active state when not provided by orchestrator
	const computedActive =
		isActive !== undefined ? isActive : isNavItemActive(item, currentPath);
	const computedLeafActive =
		isLeafActive !== undefined
			? isLeafActive
			: isNavItemActive(item, currentPath);

	const isExternal = item.target === '_blank';
	const activeClass = computedActive ? 'ws-nav-drawer__nav-item--active' : '';
	const href = buildNavUrl(item);

	// Every nav link in the drawer — top level, drilldown leaf, and grouped
	// drilldown child — renders through this component, so tracking here is what
	// makes a single code path cover CR-2 for all of them. `url` is the resolved
	// href rather than `item.url` so analytics records the destination the user
	// actually navigated to, params included.
	//
	// Fires before `onLinkClick`, which closes the drawer and therefore emits its
	// own `drawer_close`. The click is the cause and the close is the effect, so
	// this ordering is what keeps them the right way round in the event stream.
	const handleClick = useCallback(() => {
		// Labels can carry CMS markup — NavItemLabel sanitizes and renders it as
		// HTML. Analytics wants the plain text the user actually read, or the raw
		// tags would land in `element`, which the legacy GA path turns into
		// `eventName`.
		const label = stripHtmlToText(item.label);

		track(DRAWER_EVENT.NAV_LINK_CLICK, {
			// Same `element`/`result` forms ws-navigation emits, so drawer links
			// and the nav types the drawer replaces aggregate together.
			element: TRACKING_CONFIG.getHeaderEl(label),
			elementCTA: label,
			result: TRACKING_CONFIG.getNavigatedResult(href),
			widgetState: WIDGET_STATE.DRAWER_OPEN,
			// `label` and `url` are also kept raw, unparsed from the strings above,
			// so CR-2's link details can be faceted directly in NRQL.
			label,
			url: href,
			depth
		});
		onLinkClick();
	}, [track, item.label, href, depth, onLinkClick]);

	return (
		<a
			href={href}
			target={item.target || '_self'}
			rel={isExternal ? 'noopener noreferrer' : undefined}
			className={setClassNames([
				'ws-nav-drawer__nav-item',
				'ws-nav-drawer__nav-item--link',
				'py-4',
				'd-flex',
				'align-items-center',
				'w-100',
				'bg-transparent',
				'text-decoration-none',
				'text-left',
				activeClass
			])}
			aria-current={computedLeafActive ? 'page' : undefined}
			onClick={handleClick}
		>
			<NavItemLabel label={item.label} className={labelClassName} />
			{isExternal && (
				<>
					<i
						className={setClassNames([
							'ddc-icon',
							'ddc-icon-launch',
							'ws-nav-drawer__nav-item-icon',
							'ddc-font-size-large',
							'd-flex',
							'flex-shrink-0',
							'align-items-center',
							'justify-content-center'
						])}
						aria-hidden="true"
					/>
					<span className="sr-only">{opensInNewTabLabel}</span>
				</>
			)}
		</a>
	);
};

NavLink.propTypes = {
	item: PropTypes.shape({
		label: PropTypes.string.isRequired,
		url: PropTypes.string,
		target: PropTypes.string,
		selected: PropTypes.bool,
		matched: PropTypes.bool
	}).isRequired,
	onLinkClick: PropTypes.func.isRequired,
	isActive: PropTypes.bool,
	isLeafActive: PropTypes.bool,
	labelClassName: PropTypes.string,
	depth: PropTypes.oneOf(Object.values(NAV_DEPTH))
};

export default NavLink;
